import { useCallback, useEffect, useRef } from 'react';
import { NavMarker } from 'react-rhumb';
import { useHistory, useLocation, useParams } from 'quartz-routing/react-router';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import ViewCertificateModal from 'academy-common-lib/features/certificate/components/ViewCertificateModal';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import PortalIdParser from 'PortalIdParser';
import UsageTracker from '../../utils/UsageTracker';
import { cleanTrackingProps } from '../../utils/awardTrackingUtils';
import { useTrackProgress } from '../../features/userProgress';
import { useFullChirpTrack } from '../../features/tracks/tracksQueries';
import { LANGUAGE_STRINGS } from '../../components/Languages';
import { isExpiringSoon } from '../../features/myTeam/utils/dateTimeUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TrackAwardModalContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const hasTrackedView = useRef(false);
  const trackId = Number(params.trackId);
  const {
    trackProgress,
    trackProgressLoading
  } = useTrackProgress(trackId, {
    skip: !params.trackId || isNaN(trackId),
    fetchPolicy: 'network-only'
  });
  const award = trackProgress === null || trackProgress === void 0 ? void 0 : trackProgress.award;
  const {
    track,
    trackLoading
  } = useFullChirpTrack(trackId);
  const linkedTrackId = award !== null && award !== void 0 && award.trackIdAward && award.trackIdAward !== trackId ? award.trackIdAward : null;
  const isAwardInvalid = !award || !track;
  const loading = trackLoading || trackProgressLoading;
  const returnUrl = `${params[0] || '/'}${location.search}`;
  const trackUsage = useCallback((action, shareType) => {
    if (!track) {
      return;
    }
    UsageTracker.trackStandalone('Award Interaction', cleanTrackingProps({
      action,
      type: 'certification',
      name: track.name,
      trackId: track.id.toString(),
      trackName: track.name,
      view: 'track-award-modal',
      shareType
    }));
  }, [track]);
  useEffect(() => {
    if (!loading && isAwardInvalid) {
      const pagePath = params[0];
      history.replace(pagePath || '/');
    }
  }, [history, isAwardInvalid, params, loading]);
  useEffect(() => {
    if (hasTrackedView.current || isAwardInvalid) {
      return;
    }
    hasTrackedView.current = true;
    trackUsage('view-modal');
  }, [isAwardInvalid, trackUsage]);
  if (!(track !== null && track !== void 0 && track.id) && loading) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {});
  }
  if (isAwardInvalid || !track) {
    return null;
  }
  const trackLanguageKey = String(track.language || '').toLowerCase();
  const linkedTrackLanguage = award === null || award === void 0 ? void 0 : award.trackAwardLanguage;
  const linkedTrackLanguageKey = String(linkedTrackLanguage || '').toLowerCase();
  const showLinkedTrackAwardInfo = linkedTrackId != null;
  const showRetakeButton = !!(award !== null && award !== void 0 && award.expiresAt && track.id && isExpiringSoon(award.expiresAt));
  const hasEndOfTrackCta = !!track.endOfTrackCtaText && !!track.endOfTrackCtaLink;
  const hasAboveContent = showLinkedTrackAwardInfo || hasEndOfTrackCta;
  const aboveCertificationContent = hasAboveContent ? /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "400",
    marginInline: "auto",
    maxInlineSize: "500px",
    paddingBlockStart: "500",
    textAlign: "center",
    children: [showLinkedTrackAwardInfo && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            "data-test-id": "track-award-modal-subheading",
            message: "learningCenter.exams.finished.passedInLinkedLanguage",
            options: {
              courseName: track.name,
              linkedLanguage: LANGUAGE_STRINGS[linkedTrackLanguageKey],
              language: LANGUAGE_STRINGS[trackLanguageKey]
            }
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(RouterLink, {
          to: `/tracks/${track.id}/exam`,
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.exams.finished.linkedExamCTA",
            options: {
              language: LANGUAGE_STRINGS[trackLanguageKey]
            }
          })
        })
      })]
    }), hasEndOfTrackCta && /*#__PURE__*/_jsx(Link, {
      href: track.endOfTrackCtaLink,
      target: "_blank",
      children: track.endOfTrackCtaText
    })]
  }) : null;

  // Pass video needs to be migrated to Hubspot video
  // https://github.com/HubSpotEngineering/AcademyLearningExperience/issues/324
  const passVideo = null;
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "COURSE_AWARD_VIEW_LOADED",
    children: /*#__PURE__*/_jsx(ViewCertificateModal, {
      aboveCertificationContent: aboveCertificationContent,
      belowCertificateContent: passVideo,
      certificateImageUrl: (award === null || award === void 0 ? void 0 : award.certificate) || '',
      credentialTitle: track.name,
      grantCode: award === null || award === void 0 ? void 0 : award.code,
      grantedCredentialId: award === null || award === void 0 ? void 0 : award.grantedCredentialId,
      onCloseClick: () => history.push(returnUrl),
      onDownloadCertificateClick: () => trackUsage('download-certificate'),
      onShareButtonClick: () => trackUsage('click-share-this-cert'),
      retakeExamLink: showRetakeButton ? `/academy/${PortalIdParser.get()}/tracks/${track.id}/exam` : undefined,
      showProfileCta: true,
      showShareButton: true
    })
  });
};
export default TrackAwardModalContainer;